(() => {
  const routeRoot = window.Shopify?.routes?.root || '/';
  const header = document.querySelector('[data-site-header]');
  let lastFocus = null;
  let scrollQueued = false;

  const syncHeader = () => {
    header?.classList.toggle('is-scrolled', window.scrollY > 32);
    scrollQueued = false;
  };
  syncHeader();
  window.addEventListener('scroll', () => {
    if (!scrollQueued) {
      scrollQueued = true;
      window.requestAnimationFrame(syncHeader);
    }
  }, { passive: true });

  const drawer = () => document.querySelector('[data-cart-drawer]');
  const openDrawer = () => {
    const element = drawer();
    if (!element) return false;
    if (!document.body.classList.contains('lux-cart-open')) lastFocus = document.activeElement;
    element.removeAttribute('inert');
    element.setAttribute('aria-hidden', 'false');
    document.body.classList.add('lux-cart-open');
    element.querySelector('[data-cart-close]:not(.lux-cart-drawer__backdrop)')?.focus();
    return true;
  };
  const closeDrawer = () => {
    const element = drawer();
    if (!element) return;
    element.setAttribute('aria-hidden', 'true');
    element.setAttribute('inert', '');
    document.body.classList.remove('lux-cart-open');
    lastFocus?.focus?.();
  };

  const showError = (form, message) => {
    let target = form.parentElement.querySelector('.cart-form-error');
    if (!target) {
      target = document.createElement('p');
      target.className = 'cart-form-error';
      target.setAttribute('role', 'alert');
      form.insertAdjacentElement('afterend', target);
    }
    target.textContent = message;
  };

  const replaceDrawer = (html) => {
    if (!html) return false;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const replacement = parsed.querySelector('#shopify-section-cart-drawer');
    const current = document.getElementById('shopify-section-cart-drawer');
    if (!replacement || !current) return false;
    current.replaceWith(replacement);
    return true;
  };

  const updateCount = async (count) => {
    if (typeof count !== 'number') {
      const response = await fetch(`${routeRoot}cart.js`, { credentials: 'same-origin' });
      if (!response.ok) return;
      count = (await response.json()).item_count;
    }
    document.querySelectorAll('[data-cart-count]').forEach((node) => { node.textContent = count; });
    document.querySelectorAll('[data-cart-open]').forEach((node) => {
      const label = node.getAttribute('aria-label') || '';
      node.setAttribute('aria-label', label.replace(/\(\d+\)$/, `(${count})`));
    });
  };

  const submitCart = async (form, endpoint, body) => {
    const button = form.querySelector('button[type="submit"]');
    const wasDisabled = button?.disabled;
    if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
    try {
      const response = await fetch(`${routeRoot}${endpoint}`, {
        method: 'POST',
        body,
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      const result = await response.json();
      if (!response.ok) {
        showError(form, result.description || result.message || response.statusText);
        return;
      }
      if (!replaceDrawer(result.sections?.['cart-drawer'])) {
        window.location.href = `${routeRoot}cart`;
        return;
      }
      await updateCount(result.item_count).catch(() => {});
      openDrawer();
    } catch (error) {
      showError(form, error.message);
    } finally {
      if (button) { button.disabled = wasDisabled; button.removeAttribute('aria-busy'); }
    }
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-cart-open]');
    if (opener && drawer()) {
      event.preventDefault();
      openDrawer();
    }
    if (event.target.closest('[data-cart-close]')) closeDrawer();
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (form.matches('[data-product-form], [data-quick-add]')) {
      event.preventDefault();
      const data = new FormData(form);
      data.append('sections', 'cart-drawer');
      data.append('sections_url', window.location.pathname + window.location.search);
      submitCart(form, 'cart/add.js', data);
    } else if (form.matches('[data-cart-change]')) {
      event.preventDefault();
      const quantity = Number(form.querySelector('[name="quantity"]').value);
      if (!Number.isInteger(quantity) || quantity < 0) return;
      const data = new FormData();
      data.append('id', form.querySelector('[name="id"]').value);
      data.append('quantity', String(quantity));
      data.append('sections', 'cart-drawer');
      data.append('sections_url', window.location.pathname + window.location.search);
      submitCart(form, 'cart/change.js', data);
    }
  });

  document.addEventListener('keydown', (event) => {
    const element = drawer();
    if (!element || element.getAttribute('aria-hidden') !== 'false') return;
    if (event.key === 'Escape') { closeDrawer(); return; }
    if (event.key !== 'Tab') return;
    const focusable = [...element.querySelectorAll('a[href], button:not([disabled]), input:not([disabled])')]
      .filter((node) => node.getClientRects().length);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });

  document.querySelectorAll('[data-product-recommendations]').forEach((container) => {
    const load = async () => {
      try {
        const response = await fetch(container.dataset.url, { credentials: 'same-origin' });
        if (!response.ok) return;
        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        const rendered = page.querySelector('[data-product-recommendations] .tm-product-editorial__related');
        if (rendered) container.replaceChildren(rendered);
      } catch (error) {
        // Recommendations are optional; purchase controls remain usable.
      }
    };
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) { observer.disconnect(); load(); }
      }, { rootMargin: '400px' });
      observer.observe(container);
    } else {
      load();
    }
  });

  document.querySelectorAll('[data-editorial-reviews]').forEach((reviews) => {
    const rating = reviews.querySelector('[data-rating-value]');
    if (rating) {
      const value = Number(rating.dataset.ratingValue);
      if (Number.isFinite(value)) rating.textContent = value.toFixed(1);
    }
    const toggle = reviews.querySelector('[data-reviews-toggle]');
    const stack = reviews.querySelector('[data-review-stack]');
    if (!toggle || !stack) return;
    toggle.addEventListener('click', () => {
      const expanded = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', String(expanded));
      stack.classList.toggle('is-collapsed', !expanded);
      toggle.querySelector('[data-reviews-toggle-label]').textContent = expanded
        ? toggle.dataset.expandedLabel : toggle.dataset.collapsedLabel;
    });
  });
})();
